import { useEffect, useCallback, useRef, useMemo } from 'react'
import { collection, query, where, doc } from 'firebase/firestore'
import type { Query } from 'firebase/firestore'
import { addDoc, deleteDoc } from '../lib/firestore'
import { auth, db } from '../lib/firebase'
import { setPerfAccountSize } from '../lib/perfAttrs'
import { useFirestoreCollection } from './useFirestore'
import { runMutation } from '../lib/firestoreMutation'
import { track } from '../lib/analytics'
import type { ArtistInterest } from '../types'
import { fbt } from 'fbtee'

const EMPTY_MAP: Map<string, string[]> = new Map()

/**
 * The artists the user follows, app-wide (profile, artist page, event cards).
 * Hearts on an event's lineup are `useEventArtistInterests`.
 */
export function useArtistInterests(
  userId: string | null,
  options?: { onMarked?: () => void },
) {
  const ref = userId
    ? (query(
        collection(db, 'artistInterests'),
        where('userId', '==', userId),
      ) as Query<ArtistInterest>)
    : null
  const { data, loading, error } = useFirestoreCollection<ArtistInterest>(ref)

  useEffect(() => {
    if (userId && !loading && userId === auth.currentUser?.uid) setPerfAccountSize(data.length)
  }, [userId, loading, data.length])

  const onMarkedRef = useRef(options?.onMarked)
  useEffect(() => {
    onMarkedRef.current = options?.onMarked
  })

  // artistId → every Firestore doc ID for it, so removal is a direct delete with
  // no preceding read. A user can end up with duplicate docs for one artist
  // (see useFriendArtistInterests); removing must delete them all.
  const artistDocIds = useMemo(() => {
    if (!userId) return EMPTY_MAP
    const map = new Map<string, string[]>()
    for (const d of data) map.set(d.artistId, [...(map.get(d.artistId) ?? []), d.id])
    return map
  }, [userId, data])
  // The latest map for callbacks that outlive a render (e.g. a toast's Undo).
  const artistDocIdsRef = useRef(artistDocIds)
  useEffect(() => {
    artistDocIdsRef.current = artistDocIds
  })

  const interestedArtistIds = useMemo(() => new Set(artistDocIds.keys()), [artistDocIds])

  const setArtistInterest = useCallback(
    async (artistId: string, marked: boolean, context?: { eventId?: string }) => {
      if (!userId) return
      const existing = artistDocIdsRef.current.get(artistId) ?? []
      if (marked === existing.length > 0) return
      await runMutation(fbt("Couldn't update artist interest", 'Error toast when toggling interest in an artist fails'), async () => {
        if (marked) {
          await addDoc(collection(db, 'artistInterests'), { userId, artistId })
          onMarkedRef.current?.()
        } else {
          await Promise.all(existing.map(id => deleteDoc(doc(db, 'artistInterests', id))))
        }
        track('artist_interest_toggled', {
          marked,
          event_id: context?.eventId,
          artist_id: artistId,
        })
      })
    },
    [userId],
  )

  const toggleArtistInterest = useCallback(
    (artistId: string, context?: { eventId?: string }) =>
      setArtistInterest(artistId, !artistDocIdsRef.current.has(artistId), context),
    [setArtistInterest],
  )

  return { interestedArtistIds, loading, error, toggleArtistInterest, setArtistInterest }
}
